import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { X, Loader2, Key } from 'lucide-react';
import UserForm from '../forms/UserForm';
import ChangePasswordModal from './ChangePasswordModal';
import { getUser, createUser, updateUser, resetUserPassword } from '../../services/api';
import { User, UserFormData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

interface UserFormModalProps {
  userId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  userId,
  isOpen,
  onClose,
  onSaved
}) => {
  const { user } = useAuth();
  const { canCreateUsers, canEditUsers, isAdmin } = usePermissions();
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPwdPrompt, setShowPwdPrompt] = useState<boolean>(false);
  const [currentPwd, setCurrentPwd] = useState<string>('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  
  // Add backdrop effect to body when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add backdrop blur class to body
      document.body.classList.add('modal-open');
      // Disable scrolling on the body
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop blur class from body
      document.body.classList.remove('modal-open');
      // Re-enable scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  const isEditMode = !!userId;
  
  // Fetch user if editing
  useEffect(() => {
    if (isOpen && isEditMode) {
      fetchUser(userId);
    }
  }, [isEditMode, userId, isOpen]);
  
  // Fetch user data from API
  const fetchUser = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUser(id);
      setUserData(data);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user data');
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (formData: UserFormData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode) {
        // Update existing user
        await updateUser(userId as string, formData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        const res = await createUser(formData);
        toast.success('User created successfully');
        if ((res as any)?.emailed_password) {
          toast.success(`Temporary password emailed to ${formData.email}`);
        }
      }
      
      setIsSubmitting(false);
      onClose();
      void onSaved();
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      // Handle detailed validation errors from the backend
      if (error.response?.data) {
        if (error.response.data.details) {
          // Handle structured validation errors
          const validationErrors = Object.entries(error.response.data.details)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(validationErrors);
          toast.error('Please fix the validation errors');
        } else if (error.response.data.error) {
          // Handle specific error message
          setError(error.response.data.error);
          toast.error(error.response.data.error);
        } else {
          setError('Failed to save user');
          toast.error('Failed to save user');
        }
      } else {
        setError('Failed to save user');
        toast.error('Failed to save user');
      }
      
      setIsSubmitting(false);
    }
  };
  
  // Check if user has permission to manage users
  const isEditingSelf = isEditMode && userId === user?.id;
  const hasPermissionToEdit = isEditingSelf || canEditUsers(); // Allow self-editing without permission
  const hasPermissionToCreate = canCreateUsers();
  
  if (!hasPermissionToEdit && !hasPermissionToCreate) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
        <div className="bg-background-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-modalOpen">
          <div className="flex items-center justify-between p-4 border-b border-border-dark">
            <h2 className="text-xl font-semibold">User Management</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 text-white"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-2">Permission Denied</h2>
                <p className="text-text-muted-dark mb-4">
                  {isEditMode 
                    ? "You don't have permission to edit user information."
                    : "You don't have permission to create new users."
                  }
                </p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-background-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-modalOpen">
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <h2 className="text-xl font-semibold">{isEditMode ? 'Edit User' : 'Add New User'}</h2>
          <div className="flex items-center gap-2">
            {isEditMode && (
              <>
                {/* Show different password options based on user permissions and target user */}
                {userData && userData.id === user?.id ? (
                  // User editing their own profile - show change password option
                  <button
                    onClick={() => setShowChangePasswordModal(true)}
                    className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-2"
                    disabled={isSubmitting}
                  >
                    <Key size={16} />
                    Change My Password
                  </button>
                ) : canEditUsers() && isAdmin() ? (
                  // Admin editing another user - show reset password option
                  <button
                    onClick={async () => {
                      try {
                        setShowPwdPrompt(true);
                      } catch (e) {
                        console.error('Reset password failed:', e);
                        toast.error('Failed to reset password');
                      } finally {
                        // leave submitting flag controlled by dialog actions
                      }
                    }}
                    className="px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center gap-2"
                    disabled={isSubmitting}
                  >
                    <Key size={16} />
                    {isSubmitting ? 'Processing...' : 'Reset Password'}
                  </button>
                ) : null}
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 text-white hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Loading state */}
          {isEditMode && isLoading ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary mb-4 mx-auto" />
                <p className="text-text-muted-dark">Loading user details...</p>
              </div>
            </div>
          ) : (
            /* User form */
            <>
              {error && (
                <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
                  {error.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < error.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              <UserForm
                initialData={userData || undefined}
                onSubmit={handleSubmit}
                isLoading={isSubmitting}
                disableRoleEdit={isEditingSelf && !canEditUsers()}
              />
            </>
          )}
        </div>
      </div>
    </div>
    {/* Current Password Prompt */}
    {showPwdPrompt && (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
        <div className="bg-background-card border border-border-dark rounded-lg w-full max-w-md p-5">
          <h3 className="text-lg font-semibold mb-3">Confirm Password Reset</h3>
          <p className="text-sm text-gray-300 mb-4">Please enter the current password to confirm reset.</p>
          <input
            type="password"
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 mb-4"
            placeholder="Current password"
          />
          <div className="flex justify-end gap-2">
            <button
              className="px-3 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600"
              onClick={() => { setShowPwdPrompt(false); setCurrentPwd(''); }}
            >
              Cancel
            </button>
            <button
              className="px-3 py-2 text-sm rounded-md bg-indigo-600 hover:bg-indigo-700"
              onClick={async () => {
                try {
                  setIsSubmitting(true);
                  // For L0 admins, use admin_override to skip current password requirement
                  const isL0Admin = user?.tenant_level === 'L0' && isAdmin();
                  await resetUserPassword(userId as string, { 
                    force_change: true, 
                    admin_override: isL0Admin,
                    ...(currentPwd ? { current_password: currentPwd } as any : {}) 
                  } as any);
                  const to = userData?.email || 'user';
                  toast.success(`Password emailed to ${to}`);
                  setShowPwdPrompt(false);
                  setCurrentPwd('');
                } catch (e) {
                  console.error('Reset password failed:', e);
                  toast.error('Failed to reset password');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Confirm Reset
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Change Password Modal */}
    <ChangePasswordModal
      isOpen={showChangePasswordModal}
      onClose={() => setShowChangePasswordModal(false)}
      onSuccess={() => {
        // Optionally refresh user data or show success message
        toast.success('Password changed successfully');
      }}
    />
    </>
  );
};

export default UserFormModal; 